import React, { useState, useEffect, useCallback } from 'react';
import { MainLayout } from '@/components/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Plus, RefreshCw, Trash2, Search, MessageSquare, CheckCircle2,
  Clock, XCircle, ChevronDown, X, Send
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const CATEGORY_COLORS = {
  UTILITY: { bg: 'bg-gray-100', text: 'text-gray-600', label: 'UTILIDADE' },
  MARKETING: { bg: 'bg-amber-900/40', text: 'text-amber-300', label: 'MARKETING' },
  AUTHENTICATION: { bg: 'bg-purple-900/40', text: 'text-purple-300', label: 'AUTENTICACAO' },
};

const STATUS_CONFIG = {
  APPROVED: { icon: CheckCircle2, color: 'text-emerald-400', bg: 'bg-emerald-500/15', label: 'Aprovado' },
  PENDING: { icon: Clock, color: 'text-amber-400', bg: 'bg-amber-500/15', label: 'Em analise' },
  REJECTED: { icon: XCircle, color: 'text-red-400', bg: 'bg-red-500/15', label: 'Rejeitado' },
};

function extractBodyText(components) {
  if (!components || !Array.isArray(components)) return '';
  const body = components.find(c => c.type === 'BODY');
  return body?.text || '';
}

function extractHeaderText(components) {
  if (!components || !Array.isArray(components)) return '';
  const header = components.find(c => c.type === 'HEADER');
  if (header?.format === 'TEXT') return header.text || '';
  return '';
}

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

// ═══════════════════════════════════════════════════════
// TEMPLATE CARD
// ═══════════════════════════════════════════════════════
function TemplateCard({ template, onDelete }) {
  const cat = CATEGORY_COLORS[template.category] || CATEGORY_COLORS.UTILITY;
  const st = STATUS_CONFIG[template.status] || STATUS_CONFIG.PENDING;
  const StIcon = st.icon;
  const bodyText = extractBodyText(template.components);
  const headerText = extractHeaderText(template.components);

  return (
    <div data-testid={`template-card-${template.name}`}
         className="bg-gray-50 border border-gray-200 rounded-xl p-5 flex flex-col justify-between hover:border-gray-300 transition-colors">
      <div>
        <div className="flex items-center justify-between mb-3">
          <span className={`text-[10px] font-bold tracking-wider uppercase ${cat.text}`}>{cat.label}</span>
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${st.bg} ${st.color}`}>
            <StIcon size={12} />
            {st.label}
          </span>
        </div>
        <h3 className="text-gray-900 font-bold text-sm mb-3">{template.name}</h3>
        {headerText && (
          <div className="bg-gray-100 rounded-lg p-3 mb-2 text-gray-600 text-xs border-l-2 border-gray-300">
            {headerText}
          </div>
        )}
        <div className="bg-gray-100 rounded-lg p-3 text-gray-500 text-xs leading-relaxed line-clamp-4">
          {bodyText || '[Sem corpo]'}
        </div>
      </div>
      <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-200">
        <span className="text-gray-400 text-[11px]">{formatDate(template.last_updated_time)}</span>
        <button
          data-testid={`delete-template-${template.name}`}
          onClick={() => onDelete(template.name)}
          className="text-gray-400 hover:text-red-500 transition-colors p-1"
          title="Deletar template"
        >
          <Trash2 size={15} />
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// CREATE TEMPLATE MODAL
// ═══════════════════════════════════════════════════════
function CreateTemplateModal({ onClose, onCreate, phoneNumberId }) {
  const [name, setName] = useState('');
  const [category, setCategory] = useState('UTILITY');
  const [bodyText, setBodyText] = useState('');
  const [headerText, setHeaderText] = useState('');
  const [footerText, setFooterText] = useState('');
  const [saving, setSaving] = useState(false);

  const handleCreate = async () => {
    if (!name.trim() || !bodyText.trim()) {
      toast.error('Nome e corpo do template são obrigatórios');
      return;
    }
    // Validate name: only lowercase, numbers, underscores
    const cleanName = name.trim().toLowerCase().replace(/[^a-z0-9_]/g, '_');

    const components = [];
    if (headerText.trim()) {
      components.push({ type: 'HEADER', format: 'TEXT', text: headerText.trim() });
    }
    components.push({
      type: 'BODY',
      text: bodyText.trim(),
      // Extract example values for variables {{1}}, {{2}}, etc.
      ...(bodyText.match(/\{\{\d+\}\}/g) ? {
        example: {
          body_text: [bodyText.match(/\{\{\d+\}\}/g).map((_, i) => `exemplo${i + 1}`)]
        }
      } : {}),
    });
    if (footerText.trim()) {
      components.push({ type: 'FOOTER', text: footerText.trim() });
    }

    setSaving(true);
    try {
      await onCreate({ name: cleanName, category, components, phone_number_id: phoneNumberId });
      onClose();
    } catch (e) {
      // error handled in parent
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-gray-50 rounded-2xl w-full max-w-lg border border-gray-300 shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <h2 className="text-gray-900 font-bold text-base">Criar Template</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-900"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-4 max-h-[65vh] overflow-y-auto">
          <div>
            <label className="text-gray-500 text-xs font-semibold block mb-1">Nome do template</label>
            <Input
              data-testid="template-name-input"
              value={name}
              onChange={e => setName(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
              placeholder="vg_pedido_recebido"
              className="bg-gray-100 border-gray-300 text-gray-900"
            />
            <p className="text-gray-400 text-[10px] mt-1">Apenas letras minusculas, numeros e underscore</p>
          </div>

          <div>
            <label className="text-gray-500 text-xs font-semibold block mb-1">Categoria</label>
            <select
              data-testid="template-category-select"
              value={category}
              onChange={e => setCategory(e.target.value)}
              className="w-full bg-gray-100 border border-gray-300 text-gray-900 rounded-lg px-3 py-2 text-sm"
            >
              <option value="UTILITY">Utilidade</option>
              <option value="MARKETING">Marketing</option>
            </select>
          </div>

          <div>
            <label className="text-gray-500 text-xs font-semibold block mb-1">Cabecalho (opcional)</label>
            <Input
              value={headerText}
              onChange={e => setHeaderText(e.target.value)}
              placeholder="Hit Grill - Novo Pedido"
              className="bg-gray-100 border-gray-300 text-gray-900"
            />
          </div>

          <div>
            <label className="text-gray-500 text-xs font-semibold block mb-1">Corpo da mensagem</label>
            <textarea
              data-testid="template-body-input"
              value={bodyText}
              onChange={e => setBodyText(e.target.value)}
              placeholder={"Ola {{1}}! Seu pedido *{{2}}* foi recebido!\n\nUse {{1}}, {{2}}, {{3}} para variaveis."}
              rows={5}
              className="w-full bg-gray-100 border border-gray-300 text-gray-900 rounded-lg px-3 py-2 text-sm resize-none"
            />
            <p className="text-gray-400 text-[10px] mt-1">Use {"{{1}}"}, {"{{2}}"}, {"{{3}}"} para variaveis (nome, pedido, valor, etc.)</p>
          </div>

          <div>
            <label className="text-gray-500 text-xs font-semibold block mb-1">Rodape (opcional)</label>
            <Input
              value={footerText}
              onChange={e => setFooterText(e.target.value)}
              placeholder="Hit Grill - Delivery e Retirada"
              className="bg-gray-100 border-gray-300 text-gray-900"
            />
          </div>

          {/* Preview */}
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <p className="text-gray-400 text-[10px] font-semibold mb-2 uppercase tracking-wider">Pre-visualizacao</p>
            {headerText && <p className="text-gray-900 text-xs font-bold mb-1">{headerText}</p>}
            <p className="text-gray-600 text-xs whitespace-pre-wrap leading-relaxed">
              {bodyText || 'Corpo da mensagem...'}
            </p>
            {footerText && <p className="text-gray-400 text-[10px] mt-2">{footerText}</p>}
          </div>
        </div>
        <div className="p-5 border-t border-gray-200 flex gap-3 justify-end">
          <Button variant="ghost" onClick={onClose} className="text-gray-500">Cancelar</Button>
          <Button
            data-testid="create-template-submit"
            onClick={handleCreate}
            disabled={saving || !name.trim() || !bodyText.trim()}
            className="bg-emerald-600 hover:bg-emerald-700 text-gray-900"
          >
            {saving ? <RefreshCw size={14} className="animate-spin mr-2" /> : <Plus size={14} className="mr-2" />}
            Criar Template
          </Button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════
export default function WhatsAppTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [stats, setStats] = useState({ total: 0, approved: 0, pending: 0, rejected: 0 });
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [selectedPhone, setSelectedPhone] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const fetchTemplates = useCallback(async (phoneId) => {
    try {
      setLoading(true);
      const params = phoneId ? { phone_number_id: phoneId } : {};
      const res = await api.get('/whatsapp/templates/list', { params });
      setTemplates(res.data.templates || []);
      setStats(res.data.stats || { total: 0, approved: 0, pending: 0, rejected: 0 });
      if (res.data.phone_numbers) setPhoneNumbers(res.data.phone_numbers);
      if (!selectedPhone && res.data.phone_number_id) setSelectedPhone(res.data.phone_number_id);
    } catch (e) {
      toast.error('Erro ao carregar templates');
    } finally {
      setLoading(false);
    }
  }, [selectedPhone]);

  useEffect(() => {
    fetchTemplates(selectedPhone);
  }, [selectedPhone]);

  const handleCreate = async (data) => {
    try {
      await api.post('/whatsapp/templates/create', data);
      toast.success('Template enviado para aprovacao da Meta!');
      fetchTemplates(selectedPhone);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao criar template');
      throw e;
    }
  };

  const handleDelete = async (name) => {
    if (!window.confirm(`Deletar template "${name}"?`)) return;
    try {
      await api.post('/whatsapp/templates/delete', { name, phone_number_id: selectedPhone });
      toast.success('Template deletado');
      fetchTemplates(selectedPhone);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao deletar');
    }
  };

  const filtered = templates.filter(t =>
    !search || t.name?.toLowerCase().includes(search.toLowerCase()) ||
    extractBodyText(t.components)?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <MainLayout>
      <div data-testid="whatsapp-templates-page" className="min-h-screen bg-white p-4 md:p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div>
            <p className="text-gray-400 text-xs">{stats.total} templates · Covercut API</p>
          </div>
          <div className="flex items-center gap-3">
            {phoneNumbers.length > 1 && (
              <select
                value={selectedPhone}
                onChange={e => setSelectedPhone(e.target.value)}
                className="bg-gray-100 border border-gray-300 text-gray-900 rounded-lg px-3 py-2 text-xs"
              >
                {phoneNumbers.map(p => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            )}
            <Button
              data-testid="refresh-templates-btn"
              variant="ghost" size="sm"
              onClick={() => fetchTemplates(selectedPhone)}
              className="text-gray-500 hover:text-gray-900"
            >
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            </Button>
            <Button
              data-testid="create-template-btn"
              onClick={() => setShowCreate(true)}
              className="bg-emerald-600 hover:bg-emerald-700 text-gray-900 text-sm"
            >
              <Plus size={16} className="mr-2" />
              Criar Template
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          {[
            { label: 'Total', value: stats.total, cls: 'text-gray-900' },
            { label: 'Aprovados', value: stats.approved, cls: 'text-emerald-400' },
            { label: 'Em analise', value: stats.pending, cls: 'text-amber-400' },
            { label: 'Rejeitados', value: stats.rejected, cls: 'text-red-400' },
          ].map(s => (
            <Card key={s.label} className="bg-gray-50 border-gray-200">
              <CardContent className="p-4">
                <p className={`text-2xl font-bold ${s.cls}`}>{s.value}</p>
                <p className="text-gray-400 text-xs mt-1">{s.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Search */}
        <div className="relative mb-5">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            data-testid="template-search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Buscar template..."
            className="pl-10 bg-gray-50 border-gray-200 text-gray-900"
          />
        </div>

        {/* Templates Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw size={24} className="animate-spin text-gray-400" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <MessageSquare size={48} className="mx-auto text-gray-300 mb-4" />
            <p className="text-gray-400">
              {search ? 'Nenhum template encontrado' : 'Nenhum template cadastrado'}
            </p>
            <Button
              onClick={() => setShowCreate(true)}
              className="mt-4 bg-emerald-600 hover:bg-emerald-700 text-gray-900"
            >
              <Plus size={16} className="mr-2" />
              Criar primeiro template
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map(t => (
              <TemplateCard key={t.id || t.name} template={t} onDelete={handleDelete} />
            ))}
          </div>
        )}

        {/* Create Modal */}
        {showCreate && (
          <CreateTemplateModal
            onClose={() => setShowCreate(false)}
            onCreate={handleCreate}
            phoneNumberId={selectedPhone}
          />
        )}
      </div>
    </MainLayout>
  );
}
